import { PrivacyCenterTrackingPurposePreview } from '@main/cm-types';
import { Switch } from '@main/core-ui';
import {
  Card,
  StyledColumnContainer,
} from '@main/privacy-center/src/components';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Preference, PreferenceList } from '../PreferenceList';
import { getConsentFromPreferences } from './getConsentFromPreferences';
import { preferenceTopicsToDefaults } from './preferenceTopicsToDefaults';

export interface Purpose {
  /** Consent status */
  consent: boolean;
  /** Preferences */
  preferences?: Preference;
}

interface PurposeListProps {
  /** purposes preview */
  purposes: PrivacyCenterTrackingPurposePreview[];
  /** existing preferences */
  value?: Record<string, Purpose>;
  /** Whether the purposes are disabled */
  disabled?: boolean;
  /** Show the Preference topics */
  showPreferenceTopics?: boolean;
  /** When true, hide preferences if not consented */
  hideIfNotConsented?: boolean;
  /** On change function */
  onChange?: (updatedRecord: Record<string, Purpose>) => void;
}

/**
 * Purpose list shown up on the Communications Preferences page
 */
export const PurposeList: React.FC<
  React.PropsWithChildren<PurposeListProps>
> = ({
  purposes,
  value = {},
  disabled = false,
  showPreferenceTopics = true,
  hideIfNotConsented = false,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const reshapedPurposes = useMemo(
    () =>
      purposes.map(
        ({ title, trackingType, displayDescription, preferenceTopics }) => ({
          trackingType,
          displayDescription: displayDescription
            ? formatMessage(displayDescription)
            : '',
          checked: value?.[trackingType]?.consent || false,
          preferenceTopics,
          hasPreferences: !!preferenceTopics?.length,
          title: formatMessage(title),
        }),
      ),
    [value, purposes, formatMessage],
  );

  return (
    <StyledColumnContainer>
      {reshapedPurposes.map((purpose, index) => (
        <Card
          key={index}
          title={purpose.title}
          titleDescription={purpose.displayDescription}
          disabled={disabled}
          titleExtras={
            <Switch
              checked={purpose.checked}
              disabled={disabled}
              onChange={(checked) => {
                onChange?.({
                  ...value,
                  [purpose.trackingType]: {
                    consent: checked,
                    preferences: preferenceTopicsToDefaults(
                      purpose.preferenceTopics || [],
                      checked,
                    ),
                  },
                });
              }}
            />
          }
        >
          {showPreferenceTopics &&
            (!hideIfNotConsented || purpose.checked) &&
            purpose.hasPreferences && (
              <StyledColumnContainer>
                <PreferenceList
                  purposeSlug={purpose.trackingType}
                  preferences={purpose.preferenceTopics!}
                  value={value?.[purpose.trackingType]?.preferences}
                  disabled={disabled}
                  onChange={(updatedPreferences) => {
                    onChange?.({
                      ...value,
                      [purpose.trackingType]: {
                        consent: getConsentFromPreferences(updatedPreferences),
                        preferences: updatedPreferences,
                      },
                    });
                  }}
                />
              </StyledColumnContainer>
            )}
        </Card>
      ))}
    </StyledColumnContainer>
  );
};
